import { useQueryClient } from '@tanstack/react-query'
import { Camera } from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'
import { FormError } from '@/components/FormError'
import { PHOTO_TYPES, preparePhoto } from '@/lib/photos'
import { uploadPhoto } from '@/lib/recipes'
import { cn } from '@/lib/utils'

// "I made this": anyone who can see a recipe adds a photo of the dish as they made it. It joins the recipe under Made
// by the family with their name and the date; it never becomes the tile and never changes the keeper's own photos.
export function MadeIt({
  recipeId,
  className,
  children,
}: {
  recipeId: string
  className?: string
  children?: ReactNode
}) {
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [added, setAdded] = useState(false)

  async function add(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      await uploadPhoto(recipeId, await preparePhoto(file), { kind: 'made' })
      await queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] })
      setAdded(true)
    } catch (e) {
      setError(e as Error)
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <span className="contents">
      <input
        ref={input}
        type="file"
        accept={PHOTO_TYPES}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid="made-it-file"
        onChange={(e) => void add(e.target.files)}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => input.current?.click()}
        className={cn(
          'inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60',
          className,
        )}
      >
        <Camera className="size-4" aria-hidden />
        {busy ? 'Adding your photo…' : (children ?? 'I made this')}
      </button>
      {added && !busy && (
        <span role="status" className="sr-only">
          Your photo is on the recipe.
        </span>
      )}
      <FormError error={error} />
    </span>
  )
}
